import { useState, useEffect } from 'react';
import {
  FaEnvelope,
  FaInstagram,
  FaFacebookF,
  FaTwitter,
  FaPinterestP,
  FaChevronLeft,
  FaChevronRight,
  FaBell,
  FaCheck,
  FaStar,
  FaShoppingBag,
} from 'react-icons/fa';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/ComingSoon.css';

const ComingSoon = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [currentSlide, setCurrentSlide] = useState(0);

  // Sample upcoming collection items - replace with your actual data
  const upcomingItems = [
    {
      id: 1,
      name: 'Urban Chic Jacket',
      description: 'A statement piece that combines modern design with timeless comfort',
      image:
        'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
    },
    {
      id: 2,
      name: 'Ethereal Evening Gown',
      description: 'Elegance redefined for your special occasions',
      image:
        'https://images.unsplash.com/photo-1566174053879-31528523f8c6?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
    },
    {
      id: 3,
      name: 'Sustainable Denim Collection',
      description: "Eco-friendly fashion that doesn't compromise on style",
      image:
        'https://images.unsplash.com/photo-1576995853123-5a10305d93c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
    },
    {
      id: 4,
      name: 'Minimalist Accessories',
      description: 'Complete your look with our carefully crafted accessories',
      image:
        'https://images.unsplash.com/photo-1601121141461-9d6647bca1ed?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
    },
  ];

  useEffect(() => {
    // Set launch date - adjust as needed
    const launchDate = new Date('2026-12-15T00:00:00');

    const calculateTimeLeft = () => {
      const difference = launchDate - new Date();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    // Update countdown every second
    const timer = setInterval(calculateTimeLeft, 1000);
    calculateTimeLeft();

    return () => clearInterval(timer);
  }, []);

  // Auto-advance the slider
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide(prev => (prev === upcomingItems.length - 1 ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(slideInterval);
  }, [upcomingItems.length]);

  const handleSubmit = async e => {
    e.preventDefault();

    // Simple validation
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Send to API endpoint
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setIsSubscribed(true);
        setError('');
        setEmail('');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Subscription failed');
      }
    } catch (error) {
      setError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentSlide(prev => (prev === upcomingItems.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev === 0 ? upcomingItems.length - 1 : prev - 1));
  };

  return (
    <div className='coming-soon-page'>
      <div className='coming-soon-hero'>
        <div className='hero-background'></div>
        <div className='hero-overlay'></div>
        
        <div className='coming-soon-container'>
          <div className='brand-section'>
            <div className='brand-logo'>
              <FaShoppingBag className='brand-icon' />
              <span>AmFashion</span>
            </div>
            <div className='brand-tagline'>
              <FaStar className='star-icon' />
              <span>Premium Fashion Experience</span>
              <FaStar className='star-icon' />
            </div>
          </div>

          <div className='main-content'>
            <h1 className='coming-soon-title'>Something Extraordinary Is Coming</h1>
            
            <p className='coming-soon-description'>
              We're crafting an exceptional fashion experience with curated collections, 
              personalized styling, and exclusive designer pieces. Be the first to discover 
              what's next in fashion.
            </p>

            <div className='countdown-section'>
              <h3 className='countdown-heading'>Launch Countdown</h3>
              <div className='countdown-timer'>
                <div className='countdown-item'>
                  <span className='countdown-number'>{timeLeft.days}</span>
                  <span className='countdown-label'>Days</span>
                </div>
                <div className='countdown-item'>
                  <span className='countdown-number'>{timeLeft.hours}</span>
                  <span className='countdown-label'>Hours</span>
                </div>
                <div className='countdown-item'>
                  <span className='countdown-number'>{timeLeft.minutes}</span>
                  <span className='countdown-label'>Minutes</span>
                </div>
                <div className='countdown-item'>
                  <span className='countdown-number'>{timeLeft.seconds}</span>
                  <span className='countdown-label'>Seconds</span>
                </div>
              </div>
            </div>

            {/* Newsletter Subscription */}
            {isSubscribed ? (
              <div className='success-section'>
                <div className='success-icon'>
                  <FaCheck />
                </div>
                <h3>You're All Set!</h3>
                <p>Thank you for subscribing! We'll notify you as soon as we launch.</p>
                <div className='success-features'>
                  <div className='feature-item'>
                    <FaBell className='feature-icon' />
                    <span>Early Access</span>
                  </div>
                  <div className='feature-item'>
                    <FaStar className='feature-icon' />
                    <span>Exclusive Offers</span>
                  </div>
                  <div className='feature-item'>
                    <FaShoppingBag className='feature-icon' />
                    <span>First to Shop</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className='newsletter-section'>
                <div className='newsletter-header'>
                  <FaBell className='newsletter-icon' />
                  <h3>Get Notified When We Launch</h3>
                  <p>Join our exclusive list for early access and special offers</p>
                </div>

                <form onSubmit={handleSubmit} className='newsletter-form'>
                  <div className='form-group'>
                    <div className='input-container'>
                      <FaEnvelope className='input-icon' />
                      <input
                        type='email'
                        placeholder='Enter your email address'
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        disabled={isLoading}
                      />
                      <button type='submit' disabled={isLoading || !email}>
                        {isLoading ? <LoadingSpinner size="small" /> : 'Notify Me'}
                      </button>
                    </div>
                    {error && <p className='error-message'>{error}</p>}
                  </div>
                </form>

                <div className='newsletter-benefits'>
                  <div className='benefit-item'>
                    <FaStar className='benefit-icon' />
                    <span>Exclusive early access</span>
                  </div>
                  <div className='benefit-item'>
                    <FaBell className='benefit-icon' />
                    <span>Launch notifications</span>
                  </div>
                  <div className='benefit-item'>
                    <FaShoppingBag className='benefit-icon' />
                    <span>Special member offers</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Collection Section */}
      <div className='preview-section'>
        <div className='preview-container'>
          <div className='section-header'>
            <h2>What's Coming</h2>
            <p>A sneak peek at our upcoming collections</p>
          </div>

          <div className='collection-slider-container'>
            <button className='slider-arrow prev' onClick={prevSlide}>
              <FaChevronLeft />
            </button>

            <div className='collection-slider'>
              {upcomingItems.map((item, index) => (
                <div
                  key={item.id}
                  className={`collection-slide ${index === currentSlide ? 'active' : ''}`}
                  style={{ transform: `translateX(${(index - currentSlide) * 100}%)` }}
                >
                  <div className='collection-image'>
                    <img src={item.image} alt={item.name} loading="lazy" />
                    <div className='image-overlay'>
                      <span>Coming Soon</span>
                    </div>
                  </div>
                  <div className='collection-info'>
                    <h4>{item.name}</h4>
                    <p>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <button className='slider-arrow next' onClick={nextSlide}>
              <FaChevronRight />
            </button>
          </div>

          <div className='slider-dots'>
            {upcomingItems.map((item, index) => (
              <button
                key={item.id}
                className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Social Links Section */}
      <div className='social-section'>
        <div className='social-container'>
          <h3>Follow Our Journey</h3>
          <p>Stay connected for the latest updates</p>
          <div className='social-links'>
            <a href='https://instagram.com' target='_blank' rel='noopener noreferrer' aria-label="Instagram">
              <FaInstagram />
            </a>
            <a href='https://facebook.com' target='_blank' rel='noopener noreferrer' aria-label="Facebook">
              <FaFacebookF />
            </a>
            <a href='https://twitter.com' target='_blank' rel='noopener noreferrer' aria-label="Twitter">
              <FaTwitter />
            </a>
            <a href='https://pinterest.com' target='_blank' rel='noopener noreferrer' aria-label="Pinterest">
              <FaPinterestP />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;
